import cx from 'classnames'
import { FieldHookConfig, useField } from 'formik'
import * as mimeMatch from 'mime-match'
import { useEffect, useState } from 'react'
import { FaUpload } from 'react-icons/fa'

type PhotoUploadFieldProps = {
  name: string
  label: React.ReactNode
  previewImgAlt: string
} & FieldHookConfig<File | Blob>

const PhotoUploadField = ({ name, label, previewImgAlt, ...props }: PhotoUploadFieldProps) => {
  const [previewObjectURL, setPreviewObjectURL] = useState(null)
  const [isConvertingHeic, setIsConvertingHeic] = useState(false)
  const [field, meta, { setValue }] = useField<File | Blob>({ name, ...props })

  const onSetFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (previewObjectURL) {
      URL.revokeObjectURL(previewObjectURL)
      setPreviewObjectURL(null)
    }

    const file = e.currentTarget.files[0]

    if (file.type === 'image/heic') {
      setIsConvertingHeic(true)

      const heic2any = await import('heic2any')

      const h = await heic2any.default({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.1,
      })
      const [blob] = Array.isArray(h) ? h : [h]

      setIsConvertingHeic(false)
      setPreviewObjectURL(URL.createObjectURL(blob))
      setValue(new File([blob], file.name, { type: 'image/jpeg' }))

      return
    }

    if (mimeMatch(file.type, 'image/*')) {
      setPreviewObjectURL(URL.createObjectURL(file))
      setValue(file)
    } else {
      // Stub to represent just the mime type
      setValue({ type: file.type })
    }
  }

  useEffect(() => {
    if (field.value) {
      const previewUrl = URL.createObjectURL(field.value)
      setPreviewObjectURL(previewUrl)

      // Clear preview URL on unmount
      return () => URL.revokeObjectURL(previewUrl)
    }
  }, [field.value])

  const error = meta.error

  return (
    <div className="field">
      <label className="label">{label}</label>

      {isConvertingHeic && (
        <>
          <div className="rapid-photo-container">
            <div className="loadable is-loading" />

            <div className="is-flex is-fullwidth is-justify-content-center mb-4">
              <div>Converting your photo...</div>
            </div>
          </div>
        </>
      )}

      {previewObjectURL && (
        <>
          <img className="rapid-photo-preview is-block" src={previewObjectURL} alt={previewImgAlt} />
          <button
            className="button is-text outlined is-small"
            onClick={() => {
              setPreviewObjectURL(null)
              URL.revokeObjectURL(previewObjectURL)
              setValue(null)
            }}
          >
            Remove image
          </button>
        </>
      )}

      {!previewObjectURL && !isConvertingHeic && (
        <label
          role="button"
          className={cx('rapid-photo-container', {
            'is-danger': meta.touched && error,
          })}
        >
          <p>Click to upload photo</p>
          <p className="mt-3">
            <FaUpload />
          </p>
          <p className="is-size-7 mt-3">We'll delete your photo after 14 days</p>

          <input className="file-input" type="file" accept="image/*,image/heic" name="photoFile" onChange={onSetFile} />
        </label>
      )}

      {meta.touched && error && <p className="help is-danger">{error}</p>}
    </div>
  )
}

export default PhotoUploadField
